import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

import React from "react";
import { useNavigate } from "react-router-dom";
import { baseURL } from "../Config/config";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import SelexSolNavbar from "./CommonC/SelexSolNavbar";

const Level3Login = () => {
  const navigate = useNavigate();

  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  const [loading, setLoading] = React.useState(false);

  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
  });

  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  const handleAuth = async () => {
    const cleanUsername = username.trim();

    if (!cleanUsername) {
      setSnackbar({
        open: true,
        message: "Please enter your username.",
      });

      return;
    }

    if (!password) {
      setSnackbar({
        open: true,
        message: "Please enter your password.",
      });

      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${baseURL}/desktop-users/login`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            username: cleanUsername,
            password: password,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        setSnackbar({
          open: true,
          message:
            result.message || "Wrong username or password.",
        });

        return;
      }

      if (!result.token || !result.user) {
        setSnackbar({
          open: true,
          message: "Invalid login response.",
        });

        return;
      }

      /*
      ----------------------------------------------------
      SAVE DESKTOP USER SESSION
      ----------------------------------------------------
      */

      sessionStorage.setItem(
        "desktopUserToken",
        result.token
      );

      sessionStorage.setItem(
        "desktopUser",
        JSON.stringify(result.user)
      );

      /*
      Example result.user:

      {
        user_id: "...",
        username: "john",
        product_ids: [
          "RX10",
          "CIDCAD"
        ]
      }
      */

      navigate("/desktop-downloads", {
        replace: true,
      });

    } catch (error) {
      console.error("Desktop login error:", error);

      setSnackbar({
        open: true,
        message:
          "Unable to connect to the server. Please try again.",
      });

    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleAuth();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#eef3f8",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <SelexSolNavbar
        isLoggedIn={
          !!sessionStorage.getItem("desktopUserToken")
        }
      />

      {/* ERROR MESSAGE */}
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        <Alert
          severity="error"
          onClose={handleSnackbarClose}
          sx={{
            fontSize: "12px",
            width: "100%",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          mt: 15,
          position: "relative",
          zIndex: 2,
          px: 2,
        }}
      >
        {/* TITLE */}

        <Typography
          sx={{
            fontSize: 30,
            mb: 3,
            fontWeight: 950,
            color: "#0078c9",
            letterSpacing: "-1.8px",
            lineHeight: 1.08,
          }}
        >
          Desktop User Login
        </Typography>

        {/* LOGIN CARD */}

        <Box
          sx={{
            width: {
              xs: "100%",
              sm: 420,
            },

            maxWidth: 420,

            boxSizing: "border-box",

            background: "#ffffff",

            borderRadius: "14px",

            padding: {
              xs: "32px",
              sm: "52px",
            },

            boxShadow:
              "0 12px 35px rgba(0,0,0,0.12)",
          }}
        >
          {/* USERNAME */}

          <Typography
            sx={{
              fontSize: 12,
              mb: 1,
              color: "#6b7280",
              letterSpacing: "1px",
            }}
          >
            USERNAME
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #d1d5db",
              mb: 3,
              mt: 2,
            }}
          >
            <PersonOutlineIcon
              sx={{
                fontSize: 18,
                color: "#9ca3af",
                mr: 1,
              }}
            />

            <TextField
              fullWidth
              variant="standard"
              placeholder="Enter username"
              value={username}
              disabled={loading}
              autoComplete="username"
              onChange={(event) =>
                setUsername(event.target.value)
              }
              onKeyDown={handleKeyDown}
              InputProps={{
                disableUnderline: true,
              }}
              inputProps={{
                style: {
                  fontSize: 16,
                },
              }}
            />
          </Box>

          {/* PASSWORD */}

          <Typography
            sx={{
              fontSize: 12,
              color: "#6b7280",
              letterSpacing: "1px",
            }}
          >
            PASSWORD
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #d1d5db",
              mb: 4,
              mt: 2,
            }}
          >
            <LockOutlinedIcon
              sx={{
                fontSize: 18,
                color: "#9ca3af",
                mr: 1,
              }}
            />

            <TextField
              fullWidth
              type={
                showPassword
                  ? "text"
                  : "password"
              }
              variant="standard"
              value={password}
              disabled={loading}
              autoComplete="current-password"
              placeholder="Enter password"
              onChange={(event) =>
                setPassword(event.target.value)
              }
              onKeyDown={handleKeyDown}
              InputProps={{
                disableUnderline: true,
              }}
              inputProps={{
                style: {
                  fontSize: 16,
                },
              }}
            />

            {showPassword ? (
              <VisibilityOffOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: "#9ca3af",
                  cursor: "pointer",
                }}
                onClick={() =>
                  setShowPassword(false)
                }
              />
            ) : (
              <VisibilityOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: "#9ca3af",
                  cursor: "pointer",
                }}
                onClick={() =>
                  setShowPassword(true)
                }
              />
            )}
          </Box>

          {/* LOGIN BUTTON */}

          <Button
            fullWidth
            disabled={loading}
            onClick={handleAuth}
            sx={{
              height: 48,

              borderRadius: "8px",

              background:
                "linear-gradient(90deg, #0b6cb8 0%, #2da2df 100%)",

              color: "#fff",

              fontWeight: 600,

              letterSpacing: "1px",

              textTransform: "uppercase",

              boxShadow:
                "0 6px 15px rgba(0,0,0,0.15)",

              "&:hover": {
                background:
                  "linear-gradient(90deg, #095c9c 0%, #1f8bc7 100%)",
              },

              "&.Mui-disabled": {
                color: "#fff",
                opacity: 0.7,
              },
            }}
          >
            {loading ? (
              <CircularProgress
                size={20}
                sx={{
                  color: "#fff",
                }}
              />
            ) : (
              "Login"
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Level3Login;